import { useState, type FormEvent } from "react";

import { useAuth, useChangePassword, useSaveAccount } from "../api/hooks";
import type { Account } from "../api/types";
import { errorText, useFlash } from "./Flash";

/** Settings > Account: display name and email (the login name), and the password. */
export function AccountSettings() {
  const user = useAuth().data?.user;
  if (!user) return null;
  return (
    <section className="panel account-settings" id="account" aria-labelledby="account-h">
      <h2 id="account-h">Account</h2>
      <div className="two-col">
        <Profile key={`${user.email}|${user.display_name}`} user={user} />
        <Password />
      </div>
    </section>
  );
}

function Profile({ user }: { user: Account }) {
  const save = useSaveAccount();
  const flash = useFlash();
  const [f, setF] = useState({ display_name: user.display_name, email: user.email, current_password: "" });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF((x) => ({ ...x, [k]: e.target.value }));
  const emailChanged = f.email.trim().toLowerCase() !== user.email;
  const changed = emailChanged || f.display_name.trim() !== user.display_name;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate(
      {
        display_name: f.display_name,
        email: f.email,
        ...(emailChanged ? { current_password: f.current_password } : {}),
      },
      {
        onSuccess: () => flash(emailChanged ? "Saved. Sign in with your new email from now on." : "Saved."),
        onError: (err) => flash(errorText(err), "error"),
      },
    );
  };

  return (
    <form onSubmit={submit}>
      <h3>Profile</h3>
      <div className="stack-fields">
        <div>
          <label htmlFor="acc-name">Display name</label>
          <input
            id="acc-name"
            required
            maxLength={80}
            autoComplete="name"
            value={f.display_name}
            onChange={set("display_name")}
          />
        </div>
        <div>
          <label htmlFor="acc-email">Email</label>
          <input
            id="acc-email"
            type="email"
            required
            autoComplete="email"
            value={f.email}
            onChange={set("email")}
          />
          <p className="small muted field-hint">You sign in with this.</p>
        </div>
        {emailChanged && (
          <div>
            <label htmlFor="acc-email-pw">Current password</label>
            <input
              id="acc-email-pw"
              type="password"
              required
              autoComplete="current-password"
              value={f.current_password}
              onChange={set("current_password")}
            />
            <p className="small muted field-hint">Needed to change your email.</p>
          </div>
        )}
      </div>
      <div className="actions">
        <button className="btn" type="submit" disabled={!changed || save.isPending}>
          Save profile
        </button>
      </div>
    </form>
  );
}

function Password() {
  const change = useChangePassword();
  const flash = useFlash();
  const empty = { current: "", next: "", confirm: "" };
  const [f, setF] = useState(empty);
  const [problem, setProblem] = useState("");
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF((x) => ({ ...x, [k]: e.target.value }));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (f.next !== f.confirm) return setProblem("The two new passwords don't match.");
    setProblem("");
    change.mutate(
      { current_password: f.current, new_password: f.next },
      {
        onSuccess: () => {
          setF(empty);
          flash("Password changed. Any other devices will need to sign in again.");
        },
        onError: (err) => setProblem(errorText(err)),
      },
    );
  };

  return (
    <form onSubmit={submit}>
      <h3>Password</h3>
      <div className="stack-fields">
        <div>
          <label htmlFor="acc-pw">Current password</label>
          <input
            id="acc-pw"
            type="password"
            required
            autoComplete="current-password"
            value={f.current}
            onChange={set("current")}
          />
        </div>
        <div>
          <label htmlFor="acc-pw-new">New password</label>
          <input
            id="acc-pw-new"
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
            value={f.next}
            onChange={set("next")}
          />
          <p className="small muted field-hint">At least 8 characters.</p>
        </div>
        <div>
          <label htmlFor="acc-pw-confirm">Confirm new password</label>
          <input
            id="acc-pw-confirm"
            type="password"
            required
            autoComplete="new-password"
            value={f.confirm}
            onChange={set("confirm")}
          />
        </div>
      </div>
      {problem && (
        <p className="small error-text" role="alert">
          {problem}
        </p>
      )}
      <div className="actions">
        <button className="btn" type="submit" disabled={change.isPending}>
          Change password
        </button>
      </div>
    </form>
  );
}
